import { useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Users } from 'lucide-react'

import {
  DesignerSidebar,
  SidebarProvider,
  useSidebar,
} from '@/components/navigation/DesignerSidebar'
import type {
  DesignerSidebarGroup,
  DesignerSidebarUser,
} from '@/components/navigation/DesignerSidebar'
import { Button } from '@/components/ui/button'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ChatHistorySection } from '../../codex-chat/ChatHistorySection'
import { ShellChat } from '../../codex-chat/ShellChat'
import { AmbientBackdrop } from './AmbientBackdrop'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { fetchInsights, INSIGHTS_CHANGED_EVENT } from '@/api/insightsApi'
import { useAuth } from '../auth/useAuth'
import type { AuthUser } from '../types'
import { routeKind, type NavItem } from './routeModel'
import styles from './AppShell.module.scss'

export function AppShell({
  authUser,
  children,
  currentPath,
  navItems,
}: {
  authUser: AuthUser | null
  children: ReactNode
  currentPath: string
  navItems: NavItem[]
}) {
  return (
    <SidebarProvider>
      <ShellBody authUser={authUser} currentPath={currentPath} navItems={navItems}>
        {children}
      </ShellBody>
    </SidebarProvider>
  )
}

// The board (`/`, `/chat`) is a fixed canvas: the sidebar floats over it, so
// window coordinates never shift when the nav opens. Every other tasks route
// (dashboards, donors, salute) is a normal scroll page that must be PUSHED by
// the live sidebar width — otherwise its content slides under the open sidebar.
function isBoardPath(pathname: string): boolean {
  return pathname === '/' || pathname === '/chat'
}

function ShellBody({
  authUser,
  children,
  currentPath,
  navItems,
}: {
  authUser: AuthUser | null
  children: ReactNode
  currentPath: string
  navItems: NavItem[]
}) {
  const inboxCount = useInboxCount(authUser != null, currentPath)
  const sidebarGroups = groupNavItems(navItems, currentPath, inboxCount)
  const currentRoute = routeKind(currentPath)
  const { collapsed: navCollapsed, width: navWidth, isDragging: navDragging } = useSidebar()
  const [chatVisible, setChatVisible] = useState(currentPath === '/chat')

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className={styles.shell}
        data-app="progetto_arca"
        data-route={currentRoute}
        data-board={isBoardPath(currentPath) ? 'true' : 'false'}
        data-chat-route={currentPath === '/chat' ? 'true' : undefined}
        data-sidebar-dragging={navDragging ? 'true' : undefined}
        // The live sidebar width feeds `.main`'s left gutter on non-board
        // tasks routes so content is pushed, not overlapped. The 52px floor
        // matches COLLAPSED_WIDTH in DesignerSidebar.tsx.
        style={
          {
            '--sidebar-live-width': `${navCollapsed ? 52 : navWidth}px`,
          } as CSSProperties
        }
      >
        <AmbientBackdrop />
        <DesignerSidebar
          chatSection={
            authUser ? <ChatHistorySection conversationVisible={chatVisible} /> : undefined
          }
          groups={sidebarGroups}
          homeLabel="Vai alla pagina iniziale"
          showGroupHeaders
          user={authUser ? userForSidebar(authUser) : null}
          footerAction={authUser ? <FooterActions authUser={authUser} /> : null}
        />

        <main className={styles.main}>
          <div className={styles.content}>
            <div className={styles.contentStack}>{children}</div>
          </div>
        </main>

        {authUser ? (
          <ShellChat
            agentName={authUser.agent_name}
            onPresentationChange={setChatVisible}
          />
        ) : null}
      </div>
    </TooltipProvider>
  )
}

function FooterActions({ authUser }: { authUser: AuthUser }) {
  const navigate = useNavigate()
  return (
    <>
      {authUser.is_admin ? (
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => navigate('/admin/users')}
          aria-label="Gestione utenti"
          title="Gestione utenti"
        >
          <Users aria-hidden />
          <span>Utenti</span>
        </Button>
      ) : null}
      <LogoutButton />
    </>
  )
}

function LogoutButton() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [open, setOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  async function handleLogout(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    if (loggingOut) return
    setLoggingOut(true)
    try {
      await logout()
    } finally {
      navigate('/login', { replace: true })
      setLoggingOut(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="sm"
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Esci"
        disabled={loggingOut}
      >
        <LogOut aria-hidden />
        <span>{loggingOut ? 'Uscita...' : 'Esci'}</span>
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Uscire da Progetto Arca?</AlertDialogTitle>
          <AlertDialogDescription>
            Confermando terminerai la sessione corrente e tornerai alla pagina di accesso.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loggingOut}>Annulla</AlertDialogCancel>
          <AlertDialogAction intent="destructive" disabled={loggingOut} onClick={handleLogout}>
            {loggingOut ? 'Uscita...' : 'Esci'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// Open Luma work count. Refetched on every route change so reading or triaging
// updates the badge as soon as the user navigates away.
function useInboxCount(enabled: boolean, currentPath: string): number {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (!enabled) return
    let request = 0
    const refresh = () => {
      const currentRequest = ++request
      fetchInsights()
        .then((insights) => {
          if (currentRequest === request) {
            setCount(insights.filter((insight) => insight.triage === null && !insight.readAt).length)
          }
        })
        .catch(() => {
          if (currentRequest === request) setCount(0)
        })
    }
    refresh()
    window.addEventListener(INSIGHTS_CHANGED_EVENT, refresh)
    return () => {
      request++
      window.removeEventListener(INSIGHTS_CHANGED_EVENT, refresh)
    }
  }, [enabled, currentPath])
  return count
}

function groupNavItems(
  items: NavItem[],
  currentPath: string,
  inboxCount: number,
): DesignerSidebarGroup[] {
  const groups: DesignerSidebarGroup[] = []
  for (const item of items) {
    let group = groups.find((candidate) => candidate.label === item.group)
    if (!group) {
      group = { label: item.group, items: [] }
      groups.push(group)
    }
    group.items.push({
      id: item.id,
      href: item.href,
      icon: item.icon,
      label: item.label,
      statusLabel: item.statusLabel,
      notificationCount: item.id === 'insights' ? inboxCount : undefined,
      title: item.title,
      active: item.matches(currentPath),
      end: item.href === '/',
    })
  }
  return groups
}

function userForSidebar(user: AuthUser): DesignerSidebarUser {
  const displayName = user.name || user.email
  const initials = displayName
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

  return {
    initials: initials || 'U',
    displayName,
    href: '/profile',
    active: false,
  }
}
